import React, { useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Collapse } from "reactstrap";

/**
 * 摺疊區塊（design system primitive，Figma v2 `AccodrionTitle` 2128:23551 /
 * `AccordionBody` 2128:23557）。
 *
 * 設計稿的樣子跟 Bootstrap 的 `.accordion` 不一樣(Bootstrap 有自己的底色、圓角與
 * 背景圖 chevron),所以這裡自己實作外觀,不吃 `.accordion`:
 *   標題列 `#f8f9fa` 底 + 1px `#ced4da` 框 + padding 16 + gap 16,右側 chevron 16px
 *   內容區 1px `#ced4da` 框 + padding 16
 *   標題 16px 600、旁邊的統計文字 12px `#6c757d`
 *
 *   <Accordion>
 *     <AccordionItem title="機房" meta="已選 0／共 3 筆" lead={<Checkbox … />}>
 *       …
 *     </AccordionItem>
 *   </Accordion>
 *
 * `lead` 放在標題列最左邊、**在展開按鈕之外** —— 設計稿那裡是一個勾選框,
 * 把可互動元素塞進 <button> 裡是無效的 HTML,點勾選框也會順便把區塊收起來。
 *
 * 開合可受控(給 `open` + `onToggle`)或不受控(給 `defaultOpen`)。
 */
export const AccordionItem = ({
  title,
  meta,
  lead,
  open,
  defaultOpen = false,
  onToggle,
  children,
  className,
}) => {
  const [innerOpen, setInnerOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const expanded = isControlled ? open : innerOpen;

  const toggle = () => {
    if (isControlled) onToggle && onToggle(!expanded);
    else setInnerOpen((v) => !v);
  };

  return (
    <div className={classnames("dck-accordion-item", className)}>
      <div className="dck-accordion-title">
        {lead ? <span className="dck-accordion-lead">{lead}</span> : null}
        <button
          type="button"
          className="dck-accordion-toggle"
          aria-expanded={expanded}
          onClick={toggle}
        >
          <span className="dck-accordion-heading">{title}</span>
          {meta ? <span className="dck-accordion-meta">{meta}</span> : null}
          <i
            className={classnames("bx dck-accordion-chevron", expanded ? "bx-chevron-up" : "bx-chevron-down")}
            aria-hidden="true"
          />
        </button>
      </div>
      <Collapse isOpen={expanded}>
        <div className="dck-accordion-body">{children}</div>
      </Collapse>
    </div>
  );
};
AccordionItem.propTypes = {
  title: PropTypes.node,
  /** 標題右邊的次要說明,例如「已選 0／共 3 筆」 */
  meta: PropTypes.node,
  /** 標題列最左邊的元素(設計稿是勾選框);獨立於展開按鈕之外 */
  lead: PropTypes.node,
  /** 受控:目前是否展開 */
  open: PropTypes.bool,
  /** 不受控時的初始狀態 */
  defaultOpen: PropTypes.bool,
  /** 受控:(next: boolean) => void */
  onToggle: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
};

export const Accordion = ({ children, className }) => (
  <div className={classnames("dck-accordion", className)}>{children}</div>
);
Accordion.propTypes = { children: PropTypes.node, className: PropTypes.string };

export default Accordion;
